import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Search, Radar } from "lucide-react";
import { CategoryResults, QuickAdd, CampaignList, CallList, deepLinks } from "@/components/ProspectorParts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CallBoard from "@/components/CallBoard";

function Usage({ u, packs }) {
  const buy = async (n) => {
    try { const { data } = await api.post("/prospector/buy-searches", { searches: n, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start checkout"); }
  };
  if (!u) return null;
  const out = u.remaining === 0;
  return (
    <div className={`rounded-xl border p-3 text-sm ${out ? "border-sam-crimson bg-sam-crimson/5" : "border-border bg-white"}`} data-testid="prospector-usage">
      <div className="font-semibold text-sam-navy">{u.cap === null ? "Unlimited searches" : `${u.used} of ${u.cap} searches used this month`}{u.extra_credits > 0 && ` · ${u.extra_credits} extra`}</div>
      {out && <p className="mt-1 font-semibold text-sam-crimson" data-testid="prospector-limit-msg">Monthly search limit reached — ask your manager</p>}
      {u.cap !== null && (
        <div className="mt-2 flex flex-wrap gap-2">
          {packs.map((p) => <Button key={p.searches} size="sm" variant="outline" className="h-7 text-xs" onClick={() => buy(p.searches)} data-testid={`buy-pack-${p.searches}`}>Buy {p.searches} searches · ${p.price.toFixed(2)}</Button>)}
        </div>
      )}
    </div>
  );
}

export default function Prospector() {
  const [me, setMe] = useState(null);
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState(5);
  const [cats, setCats] = useState([]);
  const [incTaken, setIncTaken] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);

  const loadRows = useCallback(() => { api.get("/prospector/prospects").then(({ data }) => setRows(data)).catch(() => {}); setTick((t) => t + 1); }, []);
  useEffect(() => {
    document.title = "Route Prospector | Saving America Money";
    api.get("/prospector/me").then(({ data }) => { setMe(data); setZip(data.zips[0]?.zip || ""); }).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load the Prospector"));
    loadRows();
  }, [loadRows]);

  const search = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/prospector/search", { zip, radius_mi: radius, categories: cats, include_taken: incTaken });
      setRes(data); setMe((m) => ({ ...m, usage: data.usage }));
      if (data.limit_reached) toast.error("Monthly search limit reached — ask your manager");
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); } finally { setBusy(false); }
  };

  if (err) return <div className="mx-auto max-w-3xl px-4 py-16 text-center text-sam-slate" data-testid="prospector-error">{err}</div>;
  if (!me) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-sam-navy" /></div>;
  const toggle = (c) => setCats(cats.includes(c) ? cats.filter((x) => x !== c) : [...cats, c]);
  const zipInfo = me.zips.find((z) => z.zip === zip);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10" data-testid="prospector-page">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-crimson"><Radar className="h-4 w-4" />Route Prospector</div>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-black text-sam-navy">Find the best businesses for your card</h1>
      <p className="mt-2 max-w-2xl text-sm sm:text-base text-sam-slate">Pick a ZIP, choose categories, and get the top-rated local businesses with a fit score and an opener you can send. Fill your active postcards first.</p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5 contents lg:block">
          <div className="rounded-xl border border-border bg-white p-4 space-y-4" data-testid="prospector-form">
            {!me.places_ready && (
              <div className="rounded-md bg-sam-amber/10 p-2 text-xs text-sam-navy" data-testid="places-not-ready">
                Google search isn't connected yet. Pick categories, then use these links to find businesses and add them with Quick add.
                {cats.length > 0 && zip.length === 5 && cats.map((c) => (
                  <div key={c} className="mt-1.5 flex flex-wrap items-center gap-1.5"><b className="mr-1">{c}:</b>
                    {deepLinks(c, zip).map(([n, u]) => <a key={n} href={u} target="_blank" rel="noreferrer" className="rounded-full border border-border bg-white px-2 py-0.5 font-semibold hover:bg-sam-navy/5" data-testid={`nr-link-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${n.toLowerCase()}`}>{n}</a>)}
                  </div>
                ))}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Your ZIP</Label>
                {me.zips.length ? (
                  <Select value={zip} onValueChange={setZip}>
                    <SelectTrigger data-testid="prospector-zip"><SelectValue placeholder="Pick a ZIP" /></SelectTrigger>
                    <SelectContent>{me.zips.map((z) => <SelectItem key={z.zip} value={z.zip}>{z.zip} {z.city}{z.has_card ? ` · Active card · ${z.open_spots} open` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                ) : !me.is_admin && <p className="text-sm text-sam-crimson" data-testid="prospector-no-zips">No ZIPs assigned yet. Claim your territory on your dashboard and ask your manager to approve it.</p>}
                {me.is_admin && <Input className="mt-2" placeholder="Or type any ZIP (admin)" maxLength={5} value={me.zips.some((z) => z.zip === zip) ? "" : zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))} data-testid="prospector-zip-input" />}
              </div>
              <div>
                <Label>Radius: <b data-testid="prospector-radius-value">{radius} mi</b></Label>
                <Slider className="mt-3" min={1} max={15} step={1} value={[radius]} onValueChange={(v) => setRadius(v[0])} data-testid="prospector-radius" />
              </div>
            </div>
            <div>
              <Label>Categories</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {me.categories.map((c) => (
                  <button key={c} onClick={() => toggle(c)} data-testid={`cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${cats.includes(c) ? "border-sam-navy bg-sam-navy text-white" : "border-border bg-white text-sam-navy hover:bg-sam-navy/5"}`}>{c}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-sam-slate"><Switch checked={incTaken} onCheckedChange={setIncTaken} data-testid="prospector-include-taken" />Also search categories already sold on this card</label>
              <Button onClick={search} disabled={busy || !zip || !cats.length} className="bg-sam-crimson" data-testid="prospector-search">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}Search</Button>
            </div>
          </div>
          {res && res.results.map((b) => <CategoryResults key={b.category} block={b} zip={res.zip} onSaved={() => { loadRows(); search(); }} />)}
          <Tabs defaultValue="campaign" className="order-last">
            <TabsList>
              <TabsTrigger value="campaign" data-testid="tab-campaign">My campaign ({rows.length})</TabsTrigger>
              <TabsTrigger value="calls" data-testid="tab-call-list">Call list ({rows.filter((p) => !p.email && !p.dnc).length})</TabsTrigger>
            </TabsList>
            <p className="mt-2 text-xs text-sam-slate">Prospects with an email get the invite and follow-ups by email from your campaign, with replies coming to you. Prospects without an email go on your call list.</p>
            <TabsContent value="campaign"><CampaignList rows={rows} statuses={me.statuses} onChange={loadRows} /></TabsContent>
            <TabsContent value="calls"><CallList rows={rows} statuses={me.statuses} onChange={loadRows} /></TabsContent>
          </Tabs>
        </div>
        <div className="space-y-4 lg:order-none">
          <Usage u={me.usage} packs={me.packs} />
          <CallBoard refreshKey={tick} />
          {zipInfo?.has_card && <div className="rounded-xl border border-border bg-white p-3 text-sm" data-testid="prospector-card-info"><b className="text-sam-navy">{zipInfo.route_code}</b> · {zipInfo.open_spots} open spots on this card</div>}
          <QuickAdd zip={zip} sources={me.sources} categories={me.categories} onSaved={loadRows} />
        </div>
      </div>
    </div>
  );
}
